import React, { useState, useCallback, useEffect } from 'react';
import { ProductDetail } from '../../../../types/ProductTypes';
import { SectionCard, SectionTitle } from '../../Section.styles';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import useGetProcedures from '../../../../api/useGetProcedures';
import {
    ProcedureCard,
    ProcedureTitle,
    ProcedureFileLink,
    ProcedureDescription,
    CommentsSection,
    CommentsLabel,
    CommentsText,
    NoProceduresMessage,
    ProcedureMeta,
    CategoryLabel,
    EditSelect,
    EditTextarea,
    RemoveProcedureButton,
    ProcedureCardHeader,
    AddProcedureWrapper,
    CommentsEditSection,
} from './OtherProceduresSection.styles';

interface OtherProceduresSectionProps {
    product: ProductDetail;
}

// Other procedure categories: 2=Prewash, 5=Sampling, 6=Wallwash, 7=Charterer
const ALL_OTHER_PROCEDURE_CATEGORIES = [2, 5, 6, 7];

// Map procedure category to readable name
const getCategoryName = (category: number): string => {
    const categoryMap: { [key: number]: string } = {
        1: 'Cleaning',
        2: 'Prewash',
        3: 'Operational',
        4: 'Emergency',
        5: 'Sampling',
        6: 'Wallwash',
        7: 'Charterer',
    };
    return categoryMap[category] || `Category ${category}`;
};

const OtherProceduresSection: React.FC<OtherProceduresSectionProps> = ({ product }) => {
    const { downloadFile } = useDownloadFile();
    const { isInEditMode } = useEditMode();
    const { canEditChemGuard } = useAuthStore();
    const { mergePendingChanges } = useProductEdit();

    // Local state for edit mode
    const [localProcedures, setLocalProcedures] = useState<any[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
    const [selectedProcedureId, setSelectedProcedureId] = useState('');
    const [localOtherComments, setLocalOtherComments] = useState('');
    const [localOtherCommentsMSG, setLocalOtherCommentsMSG] = useState('');

    // Get procedures from API for the selected category
    const { getProcedures, procedures: allProceduresForCategory, loading: loadingProcedures } = useGetProcedures(selectedCategory || undefined);

    const showEditUI = isInEditMode;

    // Initialize local state when entering edit mode
    useEffect(() => {
        if (showEditUI) {
            setLocalProcedures(product.procedures?.filter((p) => ALL_OTHER_PROCEDURE_CATEGORIES.includes(p.category)) || []);
            setLocalOtherComments(product.comments?.find((c) => c.category === 10)?.text || '');
            setLocalOtherCommentsMSG(product.comments?.find((c) => c.category === 16)?.text || '');
        }
    }, [showEditUI, product]);

    // Fetch procedures when category changes
    useEffect(() => {
        if (selectedCategory && showEditUI) {
            getProcedures();
        }
    }, [selectedCategory, getProcedures, showEditUI]);

    // Filter for Other Procedures (Categories 2, 5, 6, 7 only)
    const otherProcedures = showEditUI
        ? localProcedures
        : product.procedures?.filter((p) => ALL_OTHER_PROCEDURE_CATEGORIES.includes(p.category)) || [];

    // Get comments for Other Procedures (Category 10 and 16)
    const otherProceduresComments = product.comments?.find((c) => c.category === 10);
    const otherProceduresMsgComments = product.comments?.find((c) => c.category === 16);

    // Get selectable categories (ones that don't already have a procedure)
    const getSelectableCategories = useCallback(() => {
        const usedCategories = localProcedures.map((p) => p.category);
        return ALL_OTHER_PROCEDURE_CATEGORIES.filter((cat) => !usedCategories.includes(cat));
    }, [localProcedures]);

    // Get available procedures for dropdown (exclude already added ones)
    const getAvailableProcedures = useCallback(() => {
        if (!allProceduresForCategory) return [];
        return allProceduresForCategory.filter((p: any) => !localProcedures.some((lp) => lp.id === p.id));
    }, [allProceduresForCategory, localProcedures]);

    // Handler for category change
    const handleCategoryChange = useCallback((value: string) => {
        const categoryNum = value ? parseInt(value, 10) : '';
        setSelectedCategory(categoryNum);
        setSelectedProcedureId('');
    }, []);

    // Handler for adding a procedure
    const handleAddProcedure = useCallback(
        (procedureId: string) => {
            if (!procedureId || !selectedCategory) return;

            const procedureToAdd = allProceduresForCategory?.find((p: any) => p.id.toString() === procedureId);
            if (!procedureToAdd) return;

            // Check if procedure already exists
            if (localProcedures.some((p: any) => p.id === procedureToAdd.id)) {
                setSelectedProcedureId('');
                return;
            }

            const updatedProcedures = [...localProcedures, { ...procedureToAdd, category: selectedCategory }];
            setLocalProcedures(updatedProcedures);
            setSelectedProcedureId('');
            setSelectedCategory('');

            if (mergePendingChanges) {
                // Merge with existing non-other procedures
                const otherExistingProcedures = product.procedures?.filter((p) => !ALL_OTHER_PROCEDURE_CATEGORIES.includes(p.category)) || [];
                mergePendingChanges({
                    procedures: [...otherExistingProcedures, ...updatedProcedures],
                });
            }
        },
        [allProceduresForCategory, localProcedures, selectedCategory, mergePendingChanges, product.procedures]
    );

    // Handler for removing a procedure
    const handleRemoveProcedure = useCallback(
        (procedureId: number) => {
            const updatedProcedures = localProcedures.filter((p: any) => p.id !== procedureId);
            setLocalProcedures(updatedProcedures);

            if (mergePendingChanges) {
                // Merge with existing non-other procedures
                const otherExistingProcedures = product.procedures?.filter((p) => !ALL_OTHER_PROCEDURE_CATEGORIES.includes(p.category)) || [];
                mergePendingChanges({
                    procedures: [...otherExistingProcedures, ...updatedProcedures],
                });
            }
        },
        [localProcedures, mergePendingChanges, product.procedures]
    );

    // Handler for Other Comments change
    const handleOtherCommentsChange = useCallback(
        (value: string) => {
            setLocalOtherComments(value);
            if (mergePendingChanges) {
                const existingComment = product.comments?.find((c) => c.category === 10);
                const updatedComment = {
                    id: existingComment?.id || 0,
                    text: value,
                    category: 10,
                    visibility: existingComment?.visibility || 0,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                mergePendingChanges({
                    comments: [updatedComment],
                });
            }
        },
        [mergePendingChanges, product.comments]
    );

    // Handler for Other Comments MSG change
    const handleOtherCommentsMSGChange = useCallback(
        (value: string) => {
            setLocalOtherCommentsMSG(value);
            if (mergePendingChanges) {
                const existingComment = product.comments?.find((c) => c.category === 16);
                const updatedComment = {
                    id: existingComment?.id || 0,
                    text: value,
                    category: 16,
                    visibility: existingComment?.visibility || 0,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                mergePendingChanges({
                    comments: [updatedComment],
                });
            }
        },
        [mergePendingChanges, product.comments]
    );

    // Handle file download
    const handleFileClick = (filePath: string, e: React.MouseEvent) => {
        e.preventDefault();
        downloadFile({ category: 'Procedure', fileName: filePath });
    };

    const selectableCategories = getSelectableCategories();
    const availableProcedures = getAvailableProcedures();

    return (
        <SectionCard>
            <SectionTitle>Other Procedures</SectionTitle>

            {/* Add Procedure UI - Edit Mode Only */}
            {showEditUI && selectableCategories.length > 0 && (
                <AddProcedureWrapper>
                    <span>Select a category:</span>
                    <EditSelect value={selectedCategory} onChange={(e) => handleCategoryChange(e.target.value)}>
                        <option value="">-- Select Category --</option>
                        {selectableCategories.map((cat) => (
                            <option key={cat} value={cat}>
                                {getCategoryName(cat)}
                            </option>
                        ))}
                    </EditSelect>

                    <>
                        <span>Select a procedure:</span>
                        <EditSelect
                            value={selectedProcedureId}
                            onChange={(e) => {
                                setSelectedProcedureId(e.target.value);
                                handleAddProcedure(e.target.value);
                            }}
                            disabled={!selectedCategory || loadingProcedures}
                        >
                            <option value="">
                                {selectedCategory ? '-- Select Procedure --' : 'Select a category first'}
                            </option>
                            {selectedCategory &&
                                availableProcedures.map((proc: any) => (
                                    <option key={proc.id} value={proc.id}>
                                        {proc.title} ({proc.id})
                                    </option>
                                ))}
                        </EditSelect>
                    </>
                </AddProcedureWrapper>
            )}

            {/* Other Procedures List */}
            {otherProcedures.length === 0 ? (
                <NoProceduresMessage>No other procedures available.</NoProceduresMessage>
            ) : (
                otherProcedures.map((procedure) => (
                    <ProcedureCard key={procedure.id}>
                        <ProcedureCardHeader>
                            <ProcedureMeta>
                                <CategoryLabel>{getCategoryName(procedure.category)}</CategoryLabel>
                            </ProcedureMeta>

                            {showEditUI && (
                                <RemoveProcedureButton onClick={() => handleRemoveProcedure(procedure.id)} title="Remove procedure">
                                    🗑️
                                </RemoveProcedureButton>
                            )}
                        </ProcedureCardHeader>

                        <ProcedureTitle>
                            {procedure.title} ({procedure.id})
                        </ProcedureTitle>

                        {procedure.filePath && (
                            <ProcedureFileLink href="#" onClick={(e) => handleFileClick(procedure.filePath, e)}>
                                {procedure.filePath}
                            </ProcedureFileLink>
                        )}

                        {procedure.description && (
                            <ProcedureDescription>
                                <pre>{procedure.description}</pre>
                            </ProcedureDescription>
                        )}

                        {procedure.comments && (
                            <CommentsSection>
                                <CommentsLabel>Comments:</CommentsLabel>
                                <CommentsText>
                                    <pre>{procedure.comments}</pre>
                                </CommentsText>
                            </CommentsSection>
                        )}
                    </ProcedureCard>
                ))
            )}

            {/* Other Procedures Comments (Category 10) */}
            {showEditUI ? (
                <CommentsEditSection>
                    <CommentsLabel>Comments:</CommentsLabel>
                    <EditTextarea
                        value={localOtherComments}
                        onChange={(e) => handleOtherCommentsChange(e.target.value)}
                        rows={3}
                        placeholder="Enter comments..."
                    />
                </CommentsEditSection>
            ) : (
                otherProceduresComments &&
                otherProceduresComments.text && (
                    <CommentsSection>
                        <CommentsLabel>Comments:</CommentsLabel>
                        <CommentsText>
                            <pre>{otherProceduresComments.text}</pre>
                        </CommentsText>
                    </CommentsSection>
                )
            )}

            {/* Other Procedures Comments (MSG) (Category 16) - Editor only */}
            {showEditUI && canEditChemGuard ? (
                <CommentsEditSection>
                    <CommentsLabel>Comments (MSG):</CommentsLabel>
                    <EditTextarea
                        value={localOtherCommentsMSG}
                        onChange={(e) => handleOtherCommentsMSGChange(e.target.value)}
                        rows={3}
                        placeholder="Enter MSG comments..."
                    />
                </CommentsEditSection>
            ) : (
                !showEditUI &&
                otherProceduresMsgComments &&
                otherProceduresMsgComments.text && (
                    <CommentsSection>
                        <CommentsLabel>Comments (MSG):</CommentsLabel>
                        <CommentsText>
                            <pre>{otherProceduresMsgComments.text}</pre>
                        </CommentsText>
                    </CommentsSection>
                )
            )}
        </SectionCard>
    );
};

export default OtherProceduresSection;
